import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, Calendar, Users, Target, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { projectsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ProjectImportModal = ({ open, onOpenChange }) => {
  const [state, setState] = useState('upload'); // 'upload', 'preview', 'importing', 'error'
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const resetState = () => {
    setState('upload');
    setFile(null);
    setPreviewData(null);
    setErrors([]);
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.xlsx')) {
      setErrors([{ field: 'file', message: 'Please upload an Excel file (.xlsx)' }]);
      setState('error');
      return;
    }

    setFile(selectedFile);
    await uploadAndValidate(selectedFile);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;

    if (!droppedFile.name.endsWith('.xlsx')) {
      setErrors([{ field: 'file', message: 'Please upload an Excel file (.xlsx)' }]);
      setState('error');
      return;
    }

    setFile(droppedFile);
    await uploadAndValidate(droppedFile);
  };

  const uploadAndValidate = async (fileToUpload) => {
    setState('importing');
    setErrors([]);

    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await projectsAPI.import(formData);
      
      if (response.data.success) {
        setPreviewData(response.data.data);
        setState('preview');
      } else {
        setErrors(response.data.errors || [{ field: 'file', message: 'Unknown error occurred' }]);
        setState('error');
      }
    } catch (error) {
      console.error('Import error:', error);
      setErrors([{ field: 'file', message: error.response?.data?.detail || 'Failed to process file' }]);
      setState('error');
    }
  };

  const handleCreateProject = () => {
    if (previewData?.id) {
      toast.success('Project imported successfully!');
      onOpenChange(false);
      resetState();
      navigate(`/projects/${previewData.id}`);
    }
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/PSR_Project_Import_Template.xlsx';
    link.download = 'PSR_Project_Import_Template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { if (!open) resetState(); onOpenChange(open); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Import Project from Excel
          </DialogTitle>
          <DialogDescription>
            Upload a completed Excel template to create a new project quickly
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {/* Upload State */}
          {state === 'upload' && (
            <div className="space-y-6">
              {/* Download Template */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Download className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-900">Need the template?</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Download our Excel template with all the fields pre-formatted
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={downloadTemplate}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </div>
              </div>

              {/* Upload Zone */}
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                <p className="text-lg font-medium text-slate-700">
                  Drop your Excel file here
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  or click to browse
                </p>
                <p className="text-xs text-slate-400 mt-3">
                  Accepts .xlsx files only
                </p>
              </div>

              {/* Google Sheets Tip */}
              <p className="text-xs text-slate-500 text-center">
                <span className="font-medium">Using Google Sheets?</span> Open the template in Sheets, fill it out, then go to{' '}
                <span className="font-medium">File → Download → Microsoft Excel (.xlsx)</span> before uploading.
              </p>
            </div>
          )}

          {/* Importing/Loading State */}
          {state === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
              <p className="text-lg font-medium text-slate-700">Processing file...</p>
              <p className="text-sm text-slate-500 mt-1">Validating and parsing your data</p>
            </div>
          )}

          {/* Error State */}
          {state === 'error' && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  We found some issues with your file. Please fix them and try again.
                </AlertDescription>
              </Alert>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-red-800 mb-2">Validation Errors:</p>
                <ul className="space-y-1">
                  {errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-700 flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">•</span>
                      <span>
                        <strong>{error.field}:</strong> {error.message}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button 
                variant="outline" 
                onClick={() => { resetState(); }}
                className="w-full"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Preview State */}
          {state === 'preview' && previewData && (
            <div className="space-y-4">
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  File validated successfully! Review the data below and click "Create Project" to import.
                </AlertDescription>
              </Alert>

              {/* Project Basics */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">{previewData.name}</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-500" />
                    <span>{previewData.start_date} → {previewData.end_date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-slate-500" />
                    <span className="capitalize">{previewData.methodology}</span>
                  </div>
                </div>
              </div>

              {/* Summary Counts */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{previewData.milestones_count}</p>
                  <p className="text-xs text-blue-700">Milestones</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-600">{previewData.risks_count}</p>
                  <p className="text-xs text-amber-700">Risks</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{previewData.deliverables_count}</p>
                  <p className="text-xs text-green-700">Deliverables</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-purple-600">{previewData.stakeholders_count}</p>
                  <p className="text-xs text-purple-700">Stakeholders</p>
                </div>
              </div>

              <p className="text-sm text-slate-500 text-center">
                Click "Create Project" to finalize the import
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { resetState(); onOpenChange(false); }}>
            Cancel
          </Button>
          {state === 'preview' && (
            <Button onClick={handleCreateProject}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Create Project
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectImportModal;
